import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, BookOpen, ChevronRight } from 'lucide-react';
import { API } from '@/config/api';

const LatestIntelligenceFeed = ({ lang = 'en' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(`${API}/content/latest?limit=10`);
        const data = await res.json();
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const getItemLink = (item) => {
    if (item.type === 'report' && item.year && item.week_number) {
      return `/intelligence-reports/${item.year}-week-${String(item.week_number).padStart(2, '0')}`;
    }
    if (item.slug) return `/articles/${item.slug}`;
    return '/research';
  };

  if (loading) {
    return (
      <section className="mt-6 mb-6" data-testid="intelligence-feed-loading">
        <div className="h-5 w-64 bg-gray-800 rounded animate-pulse mb-3" />
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 bg-gray-900/50 rounded animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 mb-6" data-testid="intelligence-feed">
      <h2 className="text-lg md:text-xl font-bold text-white mb-3" data-testid="intelligence-feed-title">
        Latest Intelligence & Articles
      </h2>

      <div
        className="bg-gray-900/30 border border-gray-800/60 rounded-lg overflow-hidden"
        style={{ maxHeight: '320px', overflowY: 'auto' }}
        data-testid="intelligence-feed-list"
      >
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm p-4">No content available.</p>
        ) : (
          <ul className="divide-y divide-gray-800/40">
            {items.map((item, idx) => {
              const link = getItemLink(item);
              const isReport = item.type === 'report';
              return (
                <li key={idx}>
                  <Link
                    to={link}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/40 transition-colors group"
                    data-testid={`intelligence-feed-item-${idx}`}
                  >
                    {isReport ? (
                      <FileText className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    ) : (
                      <BookOpen className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    )}
                    <span className="text-xs text-gray-500 font-mono shrink-0">
                      {formatDate(item.publish_date)}
                    </span>
                    <span className="text-sm text-gray-200 group-hover:text-white transition-colors truncate">
                      {item.title}
                    </span>
                    {!isReport && item.author && (
                      <span className="text-xs text-gray-500 shrink-0 hidden md:inline">
                        by {item.author}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-2 text-right">
        <Link
          to="/research"
          className="text-amber-500 hover:text-amber-400 text-xs font-medium inline-flex items-center gap-1"
          data-testid="intelligence-feed-view-all"
        >
          View all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </section>
  );
};

export default LatestIntelligenceFeed;
